import React from 'react';
import { Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface BoostPostButtonProps {
  postId: string;
  profileId: string;
  isOwnPost: boolean;
  isBoosted: boolean;
  boostedUntil?: string | null;
  tokenBalance: number;
  onBoost: (postId: string) => Promise<boolean>;
}

const BoostPostButton: React.FC<BoostPostButtonProps> = ({
  postId,
  isOwnPost,
  isBoosted,
  boostedUntil,
  tokenBalance,
  onBoost,
}) => {
  const [isLoading, setIsLoading] = React.useState(false);

  // Only show on own posts
  if (!isOwnPost) return null;

  // Check if currently boosted
  const isCurrentlyBoosted = isBoosted && boostedUntil && new Date(boostedUntil) > new Date();

  const handleBoost = async () => {
    setIsLoading(true);
    try {
      await onBoost(postId);
    } finally {
      setIsLoading(false);
    }
  };

  if (isCurrentlyBoosted) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-400 text-xs font-medium">
              <Zap className="h-3 w-3 fill-current" />
              <span>Boosted</span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>Boosted until {new Date(boostedUntil).toLocaleString()}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const canBoost = tokenBalance > 0;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBoost}
            disabled={!canBoost || isLoading}
            className="h-7 px-2 text-xs gap-1 hover:bg-amber-500/10 hover:text-amber-600 disabled:opacity-50"
          >
            <Zap className="h-3 w-3" />
            <span>Boost</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {canBoost 
            ? 'Spend 1 token to boost this post for 24 hours'
            : 'You need boost tokens to boost posts. Earn them by giving and receiving reactions!'
          }
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default BoostPostButton;
